// Reglas del "+" y del selector "− N +": núcleo puro (probado en node) y adaptadores del DOM al final.
// Doc: _docs/frontend/catalog_presentation/storefront_quick_add.md → Decisiones (etapas 1, 3 y 1b)

// Espejo de Cart#addable_units_for; sin control de inventario (null), siempre
export function canAddMore(quantity, available) {
  return available === null || quantity < available
}

// Lo que se ve: lo confirmado por Rails más lo pendiente
export function shown(card) {
  return card.confirmed + card.pending
}

const EMPTY = Object.freeze({ confirmed: 0, pending: 0, available: null })
const unitsOf = (units, id) => Number(units[id]) || 0
const mapCards = (cards, fn) => Object.fromEntries(Object.entries(cards).map(([id, card]) => [id, fn(card, id)]))

// Regla 2: una barra confirma todas las tarjetas salvo las ocupadas (operación en vuelo)
export function confirmedByBar(cards, units, busyIds = []) {
  const busy = new Set(busyIds)
  return mapCards(cards, (card, id) => busy.has(id) ? card : { ...card, confirmed: unitsOf(units, id) })
}

// Ritmo del selector: ms mínimos entre toques aceptados y ms sin toques antes de enviar
export const TAP_INTERVAL = 120
export const SEND_DELAY = 300

// Throttle de borde inicial
export function acceptsTap(lastAt, now, interval = TAP_INTERVAL) {
  return lastAt === undefined || now - lastAt >= interval
}

// Cola de intenciones por producto, pura: recibe el mapa de tarjetas y devuelve el nuevo; una en vuelo
export class Queue {
  constructor() {
    this.ops = []
    this.inflight = null
  }

  get busyId() {
    return this.inflight?.id ?? null
  }

  isQueued(id) {
    return this.ops.some(op => op.id === id)
  }

  // Un toque: entra si lo visto y lo disponible lo permiten; suma a lo pendiente. La tarjeta viva manda:
  // el objetivo congelado de ese producto (al dejar una sección) deja de valer
  tap(cards, id, delta) {
    const card = cards[id] ?? EMPTY
    const quantity = shown(card)
    if (delta > 0 ? !canAddMore(quantity, card.available) : quantity <= 0) return null
    const queued = this.ops.find(op => op.id === id)
    if (queued) queued.frozenTarget = undefined
    return { ...cards, [id]: { ...card, pending: card.pending + delta } }
  }

  // Listo para enviar, una entrada por producto; `frozen` fija ya la cantidad objetivo (antes de un reset)
  queue(id, { form = null, frozen = false, cards = null } = {}) {
    let op = this.ops.find(other => other.id === id)
    if (!op) {
      op = { id, form, frozenTarget: undefined }
      this.ops.push(op)
    }
    if (form) op.form = form
    if (frozen && cards) op.frozenTarget = shown(cards[id] ?? EMPTY)
    return op
  }

  // Sale la siguiente con la cantidad objetivo de ahora; sin cambio frente a lo confirmado, se descarta
  take(cards) {
    if (this.inflight) return null
    while (this.ops.length > 0) {
      const op = this.ops.shift()
      const card = cards[op.id] ?? EMPTY
      const target = op.frozenTarget ?? shown(card)
      if (op.frozenTarget === undefined && target === card.confirmed) continue
      op.target = target
      this.inflight = op
      return op
    }
    return null
  }

  // Regla 1 (200): pending = visto − confirmado; un acotado se acepta si se quería eso o más; con
  // `superseded` el producto cede a la barra más nueva; con `replay` las demás tarjetas no se tocan
  ok(cards, units, { replay = false, clamped = false, superseded = false } = {}) {
    const op = this.inflight
    this.inflight = null
    return mapCards(cards, (card, id) => {
      if (id === op.id) {
        if (superseded) return { ...card, confirmed: unitsOf(units ?? {}, id), pending: 0 }
        const desired = shown(card)
        const confirmed = units ? unitsOf(units, id) : op.target
        const pending = clamped && desired >= confirmed ? 0 : desired - confirmed
        return { ...card, confirmed, pending, available: clamped ? confirmed : card.available }
      }
      return units && !replay ? { ...card, confirmed: unitsOf(units, id) } : card
    })
  }

  // 422: la intención del producto se descarta; vuelve a la barra pintada
  rejected(cards, { units = null } = {}) {
    const op = this.inflight
    this.inflight = null
    this.ops = this.ops.filter(other => other.id !== op.id)
    const card = cards[op.id]
    if (!card) return cards
    const confirmed = units ? unitsOf(units, op.id) : card.confirmed
    return { ...cards, [op.id]: { ...card, confirmed, pending: 0 } }
  }

  // Error de red: se descarta la cola y todas las tarjetas vuelven a la barra pintada, sin reintentar
  // Lo encolado de un producto deja de valer (su línea se borra); el vuelo, si lo hay, sigue
  drop(id) {
    this.ops = this.ops.filter(op => op.id !== id)
  }

  // La página se fue y el request en vuelo se cortó: lo que sigue en cola sale igual, salvo lo del mismo
  // producto (el cortado puede seguir corriendo en el servidor y las dos escrituras se pisarían en orden desconocido)
  dropInflight() {
    const id = this.inflight?.id
    this.inflight = null
    this.ops = this.ops.filter(op => op.id !== id)
  }

  failed(cards, units = null) {
    this.inflight = null
    this.ops = []
    return mapCards(cards, (card, id) => ({
      ...card, pending: 0, confirmed: units ? unitsOf(units, id) : card.confirmed
    }))
  }

  bar(cards, units) {
    return confirmedByBar(cards, units, this.busyId === null ? [] : [this.busyId])
  }

  // turbo:before-cache: el snapshot no guarda intenciones; lo encolado se envía igual
  reset(cards) {
    return mapCards(cards, card => ({ ...card, pending: 0 }))
  }

  // Objetivo pendiente de un producto: lo encolado (ya congelado) manda sobre lo en vuelo; null sin intención
  targetFor(id) {
    const queued = this.ops.find(op => op.id === id)
    if (queued?.frozenTarget !== undefined) return queued.frozenTarget
    return this.inflight?.id === id ? this.inflight.target : null
  }

  // Tarjetas que entran por un frame, con la cola ya congelada: la barra pintada confirma; con intención, su objetivo
  adopt(cards, units) {
    return mapCards(cards, (card, id) => {
      const confirmed = units ? unitsOf(units, id) : card.confirmed
      const target = this.targetFor(id)
      return { ...card, confirmed, pending: target === null ? 0 : target - confirmed }
    })
  }
}

// Última barra conocida por cuenta, con su versión; otra cuenta empieza su propia línea de tiempo.
// Doc: _docs/frontend/catalog_presentation/storefront_quick_add.md → Decisiones (versión en la barra)
export class Bars {
  constructor() {
    this.latest = null
  }

  // Anota una barra (con un clon del contenedor); true si pasó a ser la más nueva de su cuenta
  note(bar) {
    if (!bar.accountId) return false
    const accountId = String(bar.accountId)
    const version = Number(bar.version) || 0
    if (this.latest?.accountId === accountId && version <= this.latest.version) return false
    this.latest = { accountId, version, units: bar.units ?? {}, html: bar.html, element: bar.element?.cloneNode(true) ?? null }
    return true
  }

  // La última de ESA cuenta, o null
  latestFor(accountId) {
    return this.latest && accountId && this.latest.accountId === String(accountId) ? this.latest : null
  }
}

export const bars = new Bars()

// Respuesta con versión menor que la última barra: superada; `changed` si esa barra cambió al producto
export function outranked(bar, latest, id) {
  if (!bar || !latest || !(Number(bar.version) < latest.version)) return null
  return { units: latest.units, changed: unitsOf(latest.units, id) !== unitsOf(bar.units ?? {}, id) }
}

// --- DOM ---

export const CARD_SELECTOR = "form[data-card-action]"
export const BAR_ID = "floating-cart-bar-container"

const number = value => Number(value) || 0

export function readCard(form) {
  const { confirmed, pending, quantity, available } = form.dataset
  return {
    confirmed: number(confirmed ?? quantity),
    pending: number(pending),
    available: available === undefined ? null : number(available)
  }
}

// Una tarjeta por producto (un producto puede tener dos forms en la página)
export function readCards(forms) {
  const cards = {}
  for (const form of forms) cards[form.dataset.productId] = readCard(form)
  return cards
}

const setData = (form, key, value) => {
  if (value === null) delete form.dataset[key]
  else if (form.dataset[key] !== String(value)) form.dataset[key] = String(value)
}

// Pinta la tarjeta (data-quantity lo lee el CSS); escribe solo lo que cambia
export function paint(form, card) {
  const quantity = shown(card)
  setData(form, "confirmed", card.confirmed)
  setData(form, "pending", card.pending)
  setData(form, "available", card.available)
  setData(form, "quantity", quantity)
  const label = form.querySelector(".card-action__qty")
  if (label && label.textContent !== String(quantity)) label.textContent = String(quantity)
  const enabled = canAddMore(quantity, card.available)
  form.querySelectorAll("button[name='delta'][value='1']").forEach(button => { button.disabled = !enabled })
}

export function writeCards(forms, cards) {
  for (const form of forms) {
    const card = cards[form.dataset.productId]
    if (card) paint(form, card)
  }
}

// Tarjetas con opciones: solo el número del carrito (suma sus configuraciones), sin cola; cero si el producto
// ya no está. data-quantity decide entre el "+" y el número (CSS)
export const UNITS_SELECTOR = "[data-card-units]"

export function writeUnits(badges, units) {
  for (const badge of badges) {
    const quantity = unitsOf(units, badge.dataset.productId)
    setData(badge, "quantity", quantity)
    const label = badge.querySelector(".card-action__units")
    if (label && label.textContent !== String(quantity)) label.textContent = String(quantity)
  }
}

function parseUnits(raw) {
  if (raw === undefined) return null
  try {
    const units = JSON.parse(raw)
    return units && typeof units === "object" && !Array.isArray(units) ? units : null
  } catch {
    return null
  }
}

// HTML, unidades y versión de un contenedor de barra
export function readBar(container) {
  return {
    html: container.outerHTML,
    units: parseUnits(container.dataset.cartQuantitiesUnitsValue) ?? {},
    version: Number(container.dataset.cartQuantitiesVersionValue) || 0
  }
}

// Unidades de la barra pintada: lo último que se sabe del servidor
export function paintedUnits() {
  return parseUnits(document.getElementById(BAR_ID)?.dataset.cartQuantitiesUnitsValue)
}

// La barra de una respuesta turbo_stream de quick_add, o null
export function barFromStream(html) {
  const doc = new DOMParser().parseFromString(html, "text/html")
  const stream = doc.querySelector(`turbo-stream[target="${BAR_ID}"]`)
  const container = stream?.querySelector("template")?.content.firstElementChild
  if (!container || container.id !== BAR_ID) return null
  const units = parseUnits(container.dataset.cartQuantitiesUnitsValue)
  if (!units) return null
  return { container, ...readBar(container) }
}
